import React, { useState, useEffect } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import campaignParticipantService from '../../services/campaignParticipantService';
import campaignService from '../../services/campaignService';
import userService from '../../services/userService';
import trackingLinkService from '../../services/trackingLinkService';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { getProfilePlaceholder } from '../../utils/placeholders';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const Applications = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [applications, setApplications] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    campaignId: '',
    status: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [influencers, setInfluencers] = useState({});
  const [processingIds, setProcessingIds] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState({ note: '', influencerName: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!currentUser) return;

      try {
        const campaignsData = await campaignService.getCampaignsByBrandId(currentUser.uid);
        setCampaigns(campaignsData);
        
        // If there are campaigns and no campaign filter is set, set the first campaign as default
        if (campaignsData.length > 0 && !filters.campaignId) {
          setFilters(prev => ({
            ...prev,
            campaignId: campaignsData[0].id
          }));
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      }
    };

    fetchCampaigns();
  }, [currentUser]);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentUser || !filters.campaignId) return;

      try {
        setLoading(true);
        
        // Fetch applications from Firestore
        const options = {
          orderBy: 'appliedAt',
          orderDirection: 'desc'
        };
        
        // Add status filter if provided
        if (filters.status) {
          options.status = filters.status;
        }
        
        const applicationsData = await campaignParticipantService.getParticipantsByCampaignId(
          filters.campaignId, 
          options
        );
        
        // Enhance applications with draft and content review status
        const enhancedApplications = await Promise.all(applicationsData.map(async (application) => {
          // Check for draft status if approved
          if (application.status === 'approved') {
            try {
              // Fetch draft status from contentDrafts collection
              const draftsRef = collection(db, 'contentDrafts');
              const draftQuery = query(
                draftsRef,
                where('campaignId', '==', filters.campaignId),
                where('influencerId', '==', application.influencerId),
                orderBy('submittedAt', 'desc')
              );
              
              const draftSnapshot = await getDocs(draftQuery);
              if (!draftSnapshot.empty) {
                const latestDraft = draftSnapshot.docs[0].data();
                application.draftStatus = latestDraft.status || 'not_submitted';
                application.draftId = draftSnapshot.docs[0].id;
                application.draftFeedback = latestDraft.brandFeedback || '';
                application.draftSubmittedAt = latestDraft.submittedAt;
              } else {
                application.draftStatus = 'not_submitted';
              }
            } catch (error) {
              console.error('Error fetching draft status:', error);
              application.draftStatus = 'not_submitted';
            }
            
            // Fetch proof status if exists
            if (application.proofId) {
              try {
                const proofDoc = await getDoc(doc(db, 'proofs', application.proofId));
                if (proofDoc.exists()) {
                  const proofData = proofDoc.data();
                  application.contentReviewStatus = proofData.status || 'pending';
                  application.brandFeedback = proofData.brandFeedback || '';
                }
              } catch (error) {
                console.error('Error fetching proof status:', error);
              }
            }
          }
          return application;
        }));
        
        // Fetch influencer data for each application
        const influencerIds = enhancedApplications.map(app => app.influencerId);
        const uniqueInfluencerIds = [...new Set(influencerIds)];
        
        const influencerData = {};
        await Promise.all(
          uniqueInfluencerIds.map(async (id) => {
            try {
              const influencer = await userService.getUserById(id);
              influencerData[id] = influencer;
            } catch (error) {
              console.error(`Error fetching influencer ${id}:`, error);
            }
          })
        );
        
        setInfluencers(influencerData);
        setApplications(enhancedApplications);
      } catch (error) {
        console.error("Error fetching applications:", error);
        setError('Failed to load applications. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [currentUser, filters]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      setProcessingIds(prev => [...prev, applicationId]);
      
      await campaignParticipantService.updateParticipantStatus(applicationId, newStatus);
      
      // If approving, generate tracking link immediately
      if (newStatus === 'approved') {
        try {
          const application = applications.find(app => app.id === applicationId);
          if (application) {
            // Generate tracking link for this influencer-campaign combination
            await trackingLinkService.generateTrackingLink(
              application.campaignId,
              application.influencerId,
              applicationId
            );
            console.log('Tracking link generated successfully for approved application');
          }
        } catch (trackingError) {
          console.error('Error generating tracking link:', trackingError);
          // Don't fail the approval if tracking link generation fails
          // Just log the error - tracking link can be generated later as fallback
        }
      }
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus }
          : app
      ));
      
      showSuccess(`Application ${newStatus} successfully`, `The influencer application has been ${newStatus} and they have been notified.`, 3000);
      
    } catch (error) {
      console.error(`Error updating application status:`, error);
      showError(`Failed to ${newStatus} application.`, 'Please check your connection and try again.', 5000);
    } finally {
      setProcessingIds(prev => prev.filter(id => id !== applicationId));
    }
  };

  const handleExportApplications = async () => {
    try {
      if (!filters.campaignId) {
        showError('Campaign required.', 'Please select a campaign to export applications.', 5000);
        return;
      }

      // Get campaign name for filename
      const selectedCampaign = campaigns.find(c => c.id === filters.campaignId);
      const campaignName = selectedCampaign?.name || selectedCampaign?.title || 'Campaign';

      // Prepare data for Excel export
      const exportData = applications.map(application => {
        const influencer = influencers[application.influencerId] || {};
        
        const influencerDetails = influencer.influencerDetails || {};
        
        // Social media accounts - check both possible locations
        const socialMedia = influencer.socialMedia || {};
        const socialAccounts = influencerDetails.socialAccounts || {};
        
        // Followers - check both possible locations
        const followersFromMain = influencer.followers || {};
        const followersFromDetails = influencerDetails.followers || {};
        
        // Use the data from wherever it exists
        const instagramHandle = socialMedia.instagram || socialAccounts.instagram || 'N/A';
        const tiktokHandle = socialMedia.tiktok || socialAccounts.tiktok || 'N/A';
        const youtubeHandle = socialMedia.youtube || socialAccounts.youtube || 'N/A';
        
        const instagramFollowers = followersFromMain.instagram || followersFromDetails.instagram || 0;
        const tiktokFollowers = followersFromMain.tiktok || followersFromDetails.tiktok || 0;
        const youtubeFollowers = followersFromMain.youtube || followersFromDetails.youtube || 0;
        
        // Calculate total followers
        const totalFollowers = followersFromMain.total || 
          (instagramFollowers + tiktokFollowers + youtubeFollowers) || 0;

        // Handle date formatting more safely
        let appliedDate = 'N/A';
        if (application.appliedAt) {
          try {
            if (application.appliedAt.seconds) {
              // Firestore timestamp
              appliedDate = format(new Date(application.appliedAt.seconds * 1000), 'yyyy-MM-dd HH:mm:ss');
            } else if (application.appliedAt.toDate) {
              // Firestore timestamp object
              appliedDate = format(application.appliedAt.toDate(), 'yyyy-MM-dd HH:mm:ss');
            } else {
              // Regular date
              appliedDate = format(new Date(application.appliedAt), 'yyyy-MM-dd HH:mm:ss');
            }
          } catch (dateError) {
            console.error('Error formatting date:', dateError);
            appliedDate = 'Invalid Date';
          }
        }

        return {
          'Influencer Name': influencer.displayName || 'N/A',
          'Email': influencer.email || 'N/A',
          'Instagram Handle': instagramHandle,
          'Instagram Followers': instagramFollowers,
          'TikTok Handle': tiktokHandle,
          'TikTok Followers': tiktokFollowers,
          'YouTube Handle': youtubeHandle,
          'YouTube Followers': youtubeFollowers,
          'Total Followers': totalFollowers,
          'Age Range': influencer.age || influencerDetails.ageRange || 'Not Specified',
          'Gender': influencer.gender || influencerDetails.gender || 'Not Specified',
          'Location': influencer.location || influencerDetails.location || 'N/A',
          'Bio': influencer.bio || influencerDetails.bio || 'N/A',
          'Categories': Array.isArray(influencerDetails.categories) ? influencerDetails.categories.join(', ') : 
                       Array.isArray(influencer.categories) ? influencer.categories.join(', ') : 'N/A',
          'Engagement Rate': influencer.engagementRate || influencerDetails.engagementRate || 0,
          'Application Status': application.status || 'N/A',
          'Applied Date': appliedDate,
          'Application Note': application.note || 'N/A'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const colWidths = [];
      const headers = Object.keys(exportData[0] || {});
      headers.forEach((header, index) => {
        const maxLength = Math.max(
          header.length,
          ...exportData.map(row => String(row[header] || '').length)
        );
        colWidths[index] = { width: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Applications');

      // Generate filename with timestamp
      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      const filename = `${campaignName}_Applications_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      
      showSuccess(`Applications exported successfully as ${filename}`, 'The applications data has been downloaded to your device.', 4000);
      
    } catch (error) {
      console.error('Error exporting applications:', error);
      showError('Export failed.', 'Please check your connection and try again.', 5000);
    }
  };

  const filteredApplications = applications.filter(application => {
    // Filter by search term (influencer name or email)
    const influencer = influencers[application.influencerId] || {};
    const matchesSearch = !searchTerm || 
      influencer?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      influencer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application?.note?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status (if status filter is set)
    const matchesStatus = !filters.status || application.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore timestamps
      if (timestamp.toDate) {
        return format(timestamp.toDate(), 'MMM d, yyyy');
      }
      
      // Handle regular dates
      return format(new Date(timestamp), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const handleViewNote = (note, influencerName) => {
    setSelectedNote({ note: note || 'No note provided', influencerName });
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setSelectedNote({ note: '', influencerName: '' });
  };

  return (
    <div>

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Campaign Applications</h2>
        <p className="text-gray-600">Review and manage influencer applications for your campaigns</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                className="block w-full px-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Search applications by influencer name or email"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportApplications}
                disabled={!filters.campaignId || applications.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export to Excel
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Filters
              </button>
            </div>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="campaignId" className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                <Select
                  value={filters.campaignId}
                  onValueChange={(value) => handleFilterChange({ target: { name: 'campaignId', value } })}
                >
                  <SelectTrigger id="campaignId" className="w-full">
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name || campaign.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => handleFilterChange({ target: { name: 'status', value } })}
                >
                  <SelectTrigger id="status" className="w-full">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option, index) => (
                      <SelectItem key={index} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Applications List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" color="primary" />
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Try Again
          </button>
        </div>
      ) : filteredApplications.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, filteredApplications.length)}</span> of{' '}
              <span className="font-medium">{filteredApplications.length}</span> applications
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Influencer
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied Date
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Content Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th scope="col" className="relative px-4 py-3">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedApplications.map((application) => {
                  const influencer = influencers[application.influencerId] || {};
                  const isProcessing = processingIds.includes(application.id);
                  
                  return (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 overflow-hidden">
                            <img 
                              src={influencer?.profileImageUrl || getProfilePlaceholder(influencer?.displayName || 'User')} 
                              alt={influencer?.displayName || 'Influencer'} 
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = getProfilePlaceholder(influencer?.displayName || 'User');
                              }}
                            />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {influencer?.displayName || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-[150px]">
                              {influencer?.email || 'No email'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(application.appliedAt)}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(application.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {application.status === 'approved' ? (
                          <div className="space-y-1">
                            {/* Draft Status */}
                            <div>
                              {application.draftStatus === 'not_submitted' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Awaiting Draft
                                </span>
                              ) : application.draftStatus === 'pending' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Draft Pending Review
                                </span>
                              ) : application.draftStatus === 'approved' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Draft Approved
                                </span>
                              ) : application.draftStatus === 'rejected' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Draft Needs Revision
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  No Draft
                                </span>
                              )}
                            </div>
                            
                            {/* Proof Status (if draft is approved) */}
                            {application.draftStatus === 'approved' && (
                              <div>
                                {(!application.submissionStatus || application.submissionStatus !== 'submitted') ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Awaiting Proof
                                  </span>
                                ) : application.contentReviewStatus === 'approved' ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Content approved
                                  </span>
                                ) : application.contentReviewStatus === 'revision_requested' ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Revision requested
                                  </span>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      Submitted
                                    </span>
                                    {application.proofId && (
                                      <Link 
                                        to={`/brand/content-approval/${application.proofId}`}
                                        className="text-primary hover:text-primary-dark text-xs font-medium"
                                      >
                                        Review
                                      </Link>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            N/A
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div 
                          className="text-sm text-gray-900 max-w-[200px] cursor-pointer hover:text-primary transition-colors duration-200"
                          onClick={() => handleViewNote(application.note, influencer?.displayName || 'Unknown User')}
                          title="Click to view full note"
                        >
                          <span className="truncate block">
                            {application.note || 'No note provided'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {application.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleStatusUpdate(application.id, 'approved')}
                              disabled={isProcessing}
                              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(application.id, 'rejected')}
                              disabled={isProcessing}
                              className={`inline-flex items-center px-2.5 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {application.status === 'approved' && application.submissionStatus === 'submitted' && application.proofId && (
                          <Link 
                            to={`/brand/content-approval/${application.proofId}`}
                            className="inline-flex items-center px-3 py-1.5 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            Review Content
                          </Link>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <Link to={`/brand/influencers/${application.influencerId}`} className="text-primary hover:text-primary-dark">
                          View Profile <span className="sr-only">, {influencer?.displayName}</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(endIndex, filteredApplications.length)}</span> of{' '}
                    <span className="font-medium">{filteredApplications.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber
                                ? 'z-10 bg-primary border-primary text-white'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">No applications found</h3>
          <p className="text-gray-500 mb-4">
            {campaigns.length === 0 
              ? "You don't have any campaigns yet. Create a campaign to start receiving applications."
              : "No applications match your current filters."}
          </p>
          {campaigns.length === 0 && (
            <Link
              to="/brand/campaigns/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
            >
              Create Campaign
            </Link>
          )}
        </div>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Application Note - {selectedNote.influencerName}
                </h3>
                <button
                  onClick={closeNotesModal}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
              <div className="mt-4">
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedNote.note}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeNotesModal}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
